/* ============================================================================
   FuckedUpLayer.tsx — mode 'fucked up' : chaos contrôlé messages + photos.

   Tous les messages verified et photos R2 apparaissent à positions aléatoires
   sur le canvas, avec timing d'apparition / disparition cyclique. Plusieurs
   layers d'animation simultanées :
   - Messages texte : position random, type-on character by character,
     opacity fade, rotation jitter, scale pulse, RGB glitch occasionnel
   - Photos : position random, scale-in/out, rotation drift, masque clip-path
     animé, chromatic aberration burst à l'apparition
   - Trail de fragments : caractères isolés qui dérivent en arrière-plan

   Esthétique : doctrine MARTHE (grayscale, JetBrains Mono, border 1px, no
   shadow/gradient). Chaos contrôlé via seed hash de message.id (déterministe
   entre renders, mais randomisé entre messages).

   Issu du user request 2026-05-22 — v0 fonctionnelle. Patterns avancés
   (datamosh, pixel sort, etc.) à ajouter dans une v1 quand workers reviennent.
   ============================================================================ */

import { useEffect, useRef, useState } from 'react';
import type { Message } from '@shared/types';
import type { CanvasParams } from '../../lib/canvasStore';

interface Props {
  messages: Message[];
  params: CanvasParams;
}

/** Hash FNV-1a déterministe message.id → 32-bit. */
function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Slot d'un message à un instant donné — état mutable, regenerated au début
 *  de chaque cycle (toutes les CYCLE_DURATION_MS ms). */
interface Slot {
  x: number; // % du canvas, centre du rectangle
  y: number;
  halfWPct: number; // demi-largeur en % (pour collision detection)
  halfHPct: number; // demi-hauteur en % (idem)
  rotation: number; // deg
  cycleStartTs: number; // ms timestamp du début du cycle actuel
  jitterX: number;
  jitterY: number;
  isPhoto: boolean;
}

/** Durée d'un cycle complet = 60 secondes (apparition + 56s plateau + disparition). */
const CYCLE_DURATION_MS = 60_000;

/** Ratio fade-in / fade-out par rapport au cycle total : 2s / 60s = 0.033. */
const FADE_RATIO = 0.033;

/** Dimensions approximatives en px pour collision detection. Photo carrée
 *  160 + margin, texte ~280×140 (title + 6 lignes body). */
const PHOTO_BOX_PX = { w: 180, h: 180 };
const TEXT_BOX_PX = { w: 300, h: 170 };

/** Test de chevauchement entre 2 boxes (centre + demi-dim) avec marge de sécu. */
function boxesOverlap(a: Slot, b: Slot): boolean {
  const margin = 1; // 1% pour respiration visuelle entre éléments
  const dx = Math.abs(a.x - b.x);
  const dy = Math.abs(a.y - b.y);
  return dx < a.halfWPct + b.halfWPct + margin
      && dy < a.halfHPct + b.halfHPct + margin;
}

/** Génère un nouveau slot aléatoire qui ne chevauche pas les `activeSlots`.
 *  Try jusqu'à 25 positions différentes. Si toutes échouent → accepte la
 *  dernière (cas extrême avec >15 messages sur petit canvas). */
function spawnSlot(
  now: number,
  isPhoto: boolean,
  rectW: number,
  rectH: number,
  activeSlots: Iterable<Slot>,
): Slot {
  const box = isPhoto ? PHOTO_BOX_PX : TEXT_BOX_PX;
  const halfWPct = Math.min(40, (box.w / 2 / rectW) * 100);
  const halfHPct = Math.min(40, (box.h / 2 / rectH) * 100);
  const minX = halfWPct + 1;
  const maxX = 100 - halfWPct - 1;
  const minY = halfHPct + 1;
  const maxY = 100 - halfHPct - 1;
  const others = [...activeSlots];

  let candidate: Slot | null = null;
  for (let attempt = 0; attempt < 25; attempt++) {
    const x = minX + Math.random() * (maxX - minX);
    const y = minY + Math.random() * (maxY - minY);
    const trial: Slot = {
      x, y, halfWPct, halfHPct,
      rotation: (Math.random() - 0.5) * 12,
      cycleStartTs: now,
      jitterX: (Math.random() - 0.5) * 4,
      jitterY: (Math.random() - 0.5) * 4,
      isPhoto,
    };
    let collision = false;
    for (const other of others) {
      if (boxesOverlap(trial, other)) {
        collision = true;
        break;
      }
    }
    if (!collision) return trial;
    candidate = trial; // garde le dernier essai au cas où on bail out
  }
  // Fallback : accepte la dernière position malgré collision
  return candidate!;
}

/** Easing courbe in-out-quart pour smooth fade. */
function easeInOutQuart(t: number): number {
  return t < 0.5 ? 8 * t * t * t * t : 1 - Math.pow(-2 * t + 2, 4) / 2;
}

/** Opacity selon t [0..1] du cycle (durée 60s) : 2s fade-in, 56s plateau, 2s fade-out. */
function opacityForCyclePos(t: number): number {
  if (t < FADE_RATIO) return easeInOutQuart(t / FADE_RATIO);
  if (t > 1 - FADE_RATIO) return easeInOutQuart((1 - t) / FADE_RATIO);
  return 1;
}

/** Chars de scramble pour effet terminal decode (doctrine grayscale). */
const SCRAMBLE_CHARS = '█▒░▓|/\\!?#@$%*+=<>~^&-';

function scrambleChar(rng: () => number): string {
  return SCRAMBLE_CHARS[Math.floor(rng() * SCRAMBLE_CHARS.length)]!;
}

/**
 * Scramble + reveal d'un texte selon t [0..1] dans la phase fade-in.
 * - t < 0.1 : tous les chars sont random (sauf espaces)
 * - 0.1 < t < 0.18 : reveal progressif character by character avec stagger
 * - t > 0.18 : texte normal
 */
function revealText(target: string, t: number, rng: () => number): string {
  if (t < 0.1) {
    let out = '';
    for (const c of target) out += c === ' ' ? ' ' : scrambleChar(rng);
    return out;
  }
  if (t < 0.18) {
    const revealRatio = (t - 0.1) / 0.08;
    let out = '';
    for (let i = 0; i < target.length; i++) {
      const c = target[i]!;
      if (c === ' ' || c === '\n') {
        out += c;
        continue;
      }
      // Stagger : chaque char révèle quand revealRatio dépasse son seuil
      const threshold = (i / target.length) * 0.7;
      out += revealRatio > threshold ? c : scrambleChar(rng);
    }
    return out;
  }
  return target;
}

export function FuckedUpLayer({ messages, params }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const paramsRef = useRef(params);
  paramsRef.current = params;
  const msgsRef = useRef(messages);
  msgsRef.current = messages;
  // Cache photo HTMLImageElement par message.id pour éviter de re-loader
  const imgCacheRef = useRef<Map<string, HTMLImageElement>>(new Map());
  // Slots stateful : position + cycleStartTs par message. Regenerated quand
  // un cycle complet (60s) se termine, pour repositionner le message ailleurs.
  const slotsRef = useRef<Map<string, Slot>>(new Map());
  const [, setTick] = useState(0);

  // Pré-charge les photos au mount + à chaque nouveau message
  useEffect(() => {
    const cache = imgCacheRef.current;
    for (const m of messages) {
      if (m.photoUrl && !cache.has(m.id)) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = m.photoUrl;
        cache.set(m.id, img);
        img.onload = () => setTick((n) => n + 1);
      }
    }
  }, [messages]);

  useEffect(() => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    const ctx = cvs.getContext('2d');
    if (!ctx) return;

    let raf = 0;
    const startTime = performance.now();

    function tick() {
      if (!ctx) return;
      const cur = canvasRef.current;
      if (!cur) return;
      const dpr = window.devicePixelRatio || 1;
      const rect = cur.getBoundingClientRect();
      if (cur.width !== rect.width * dpr || cur.height !== rect.height * dpr) {
        cur.width = Math.max(1, Math.round(rect.width * dpr));
        cur.height = Math.max(1, Math.round(rect.height * dpr));
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      }
      const p = paramsRef.current;
      const bg = p.bgPalette === 'ink' ? '#0d0d0d' : '#ffffff';
      const fg = p.bgPalette === 'ink' ? '#ffffff' : '#0d0d0d';

      // Fade fond avec slight ghosting (pas de clear full → trails subtils)
      ctx.globalAlpha = 0.18;
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, rect.width, rect.height);
      ctx.globalAlpha = 1;

      const now = performance.now();
      const t = (now - startTime) * 0.001;
      const all = msgsRef.current;
      const slots = slotsRef.current;

      // Cleanup : remove slots dont le message n'existe plus
      const activeIds = new Set(all.map((m) => m.id));
      for (const id of [...slots.keys()]) {
        if (!activeIds.has(id)) slots.delete(id);
      }

      for (const m of all) {
        // Détermine si message est photo (image dispo dans le cache + chargée)
        const photoImg = m.photoUrl ? imgCacheRef.current.get(m.id) : null;
        const isPhoto = !!(photoImg && photoImg.complete && photoImg.naturalWidth > 0);

        // Récupère / crée le slot. Collision-aware : on passe les autres
        // slots actifs pour éviter les chevauchements lors du spawn.
        let slot = slots.get(m.id);
        if (!slot) {
          // Premier slot : phase aléatoire dans le cycle pour stagger
          const others = [...slots.values()].filter((s) => s !== slot);
          slot = spawnSlot(now - Math.random() * CYCLE_DURATION_MS, isPhoto, rect.width, rect.height, others);
          slots.set(m.id, slot);
        }
        // Cycle terminé → regenerate (nouvelle position aléatoire sans collision)
        const elapsedMs = now - slot.cycleStartTs;
        if (elapsedMs >= CYCLE_DURATION_MS) {
          // Exclut le slot actuel des others pour pouvoir réutiliser sa position
          const others = [...slots.values()].filter((s) => s !== slot);
          slot = spawnSlot(now, isPhoto, rect.width, rect.height, others);
          slots.set(m.id, slot);
        }
        const cyclePos = (now - slot.cycleStartTs) / CYCLE_DURATION_MS;
        const opacity = opacityForCyclePos(cyclePos);
        if (opacity <= 0.01) continue;

        // Drift très lent (oscillation low-freq sin/cos, ne sort jamais des bords)
        const driftX = Math.sin(t * 0.15 + slot.x) * slot.jitterX;
        const driftY = Math.cos(t * 0.12 + slot.y) * slot.jitterY;
        const cx = ((slot.x + driftX) / 100) * rect.width;
        const cy = ((slot.y + driftY) / 100) * rect.height;

        // Glitch RGB burst pour le TEXTE uniquement (user request 2026-05-22 :
        // pas de déformation sur les images). Au peak du cycle, 30% chance.
        // Pour les photos : skip totalement le glitch, image propre.
        const peakDist = Math.abs(cyclePos - 0.5);
        const textGlitchActive = peakDist < 0.05 && Math.random() < 0.3;

        ctx.save();
        ctx.translate(cx, cy);
        // Rotation appliquée UNIQUEMENT au texte (la rotation sur canvas
        // d'une image cause de l'aliasing pixel visible = "déformation").
        // Pour les photos : transform identité, image pixel-perfect.
        if (!isPhoto) {
          ctx.rotate((slot.rotation * Math.PI) / 180);
        }
        ctx.globalAlpha = opacity;

        if (isPhoto && photoImg) {
          // PHOTO : aspect ratio natif préservé (user feedback 2026-05-22).
          // Max 160px sur la dimension la plus grande, l'autre est ratio-driven.
          // Pas de rotation, pas de contrast, juste grayscale.
          const MAX_DIM = 160;
          const nW = photoImg.naturalWidth;
          const nH = photoImg.naturalHeight;
          const aspect = nW / Math.max(1, nH);
          let w: number;
          let h: number;
          if (aspect >= 1) {
            w = MAX_DIM;
            h = MAX_DIM / aspect;
          } else {
            h = MAX_DIM;
            w = MAX_DIM * aspect;
          }
          ctx.globalCompositeOperation = 'source-over';
          ctx.filter = 'grayscale(100%)';
          ctx.drawImage(photoImg, -w / 2, -h / 2, w, h);
          ctx.filter = 'none';
          // Border 1px discrète, dimensions natives
          ctx.strokeStyle = fg;
          ctx.lineWidth = 1;
          ctx.strokeRect(-w / 2, -h / 2, w, h);
        } else {
          // TEXTE : title + body avec SCRAMBLE REVEAL (terminal decode)
          const titleSize = 18;
          const bodySize = 12;
          const titleLineH = 22;
          ctx.fillStyle = fg;
          ctx.textBaseline = 'top';
          ctx.textAlign = 'center';

          // RNG seedé pour scramble (stable par message mais varie par frame
          // via timestamp). Chaque frame re-scramble visuellement.
          const scrambleSeed = hash(m.id + Math.floor(performance.now() / 80));
          let rngState = scrambleSeed;
          const rng = () => {
            rngState = (rngState * 1664525 + 1013904223) >>> 0;
            return rngState / 4294967296;
          };

          const fullTitle = m.title.slice(0, 60).toUpperCase();
          const fullBody = m.body.slice(0, 200);
          // Scramble title pendant 0..0.18 du cycle
          const displayedTitle = revealText(fullTitle, cyclePos, rng);
          // Body décalé +0.05 dans le scramble (apparaît après le title)
          const bodyT = Math.max(0, cyclePos - 0.05);
          const displayedBody = revealText(fullBody, bodyT, rng);

          // Glitch RGB-like sur title
          if (textGlitchActive) {
            ctx.font = `${titleSize}px "JetBrains Mono", monospace`;
            ctx.globalCompositeOperation = 'difference';
            ctx.fillText(displayedTitle, 2, 0);
            ctx.fillText(displayedTitle, -2, 0);
            ctx.globalCompositeOperation = 'source-over';
          }

          // Title
          ctx.font = `${titleSize}px "JetBrains Mono", monospace`;
          ctx.fillText(displayedTitle, 0, 0);

          // Body multi-line wrapping basique
          ctx.font = `${bodySize}px "JetBrains Mono", monospace`;
          const words = displayedBody.split(' ');
          const lines: string[] = [];
          let currentLine = '';
          const maxWidth = 240;
          for (const w of words) {
            const test = currentLine ? `${currentLine} ${w}` : w;
            if (ctx.measureText(test).width > maxWidth && currentLine) {
              lines.push(currentLine);
              currentLine = w;
            } else {
              currentLine = test;
            }
          }
          if (currentLine) lines.push(currentLine);
          for (let i = 0; i < lines.length && i < 6; i++) {
            ctx.fillText(lines[i]!, 0, titleLineH + i * (bodySize + 4));
          }
        }
        ctx.restore();
      }

      // Noise grain overlay — texture film fine sur tout le canvas.
      // ImageData manipulation pixel-par-pixel, échantillonné à 25% de la
      // surface pour rester perf-friendly. Alpha bas (~12) pour subtilité.
      const grainSize = 96;
      const grainCanvas = document.createElement('canvas');
      grainCanvas.width = grainSize;
      grainCanvas.height = grainSize;
      const gctx = grainCanvas.getContext('2d');
      if (gctx) {
        const gid = gctx.createImageData(grainSize, grainSize);
        const gd = gid.data;
        for (let i = 0; i < gd.length; i += 4) {
          const n = Math.random() * 255;
          gd[i] = n;
          gd[i + 1] = n;
          gd[i + 2] = n;
          gd[i + 3] = 8; // alpha réduit (was 18) — moins agressif sur les photos
        }
        gctx.putImageData(gid, 0, 0);
        const prevAlpha = ctx.globalAlpha;
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = p.bgPalette === 'ink' ? 'screen' : 'multiply';
        // Tile sur tout le canvas
        for (let gy = 0; gy < rect.height; gy += grainSize) {
          for (let gx = 0; gx < rect.width; gx += grainSize) {
            ctx.drawImage(grainCanvas, gx, gy);
          }
        }
        ctx.globalCompositeOperation = 'source-over';
        ctx.globalAlpha = prevAlpha;
      }

      raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="canvas-fucked-up"
      data-testid="canvas-fucked-up"
      aria-hidden="true"
    />
  );
}
